/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable no-console */
import { LocationStrategy, PathLocationStrategy } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Observable, timer } from 'rxjs';
import { ILogger } from 'shared-components';

import { EnvironmentService } from 'app/core/app-constants/environment.service';
import { UrlService } from 'app/core/http/url.service';
import { createCircularReplacer } from 'app/core/kh-utils/kh-utils';
import { LogLevel } from '../constants/log-level';
import { ILog } from '../models/log';

@UntilDestroy()
@Injectable({ providedIn: 'root' })
export class Logger implements ILogger {
  private minLogLevel = LogLevel.Debug;

  private static readonly LOGGED_FLAG = Symbol('khLogged');

  private static recent = new Set<string>();
  private static readonly DUP_WINDOW_MS = 1000;
  private static readonly MAX_RECENT = 200;

  private static aggregate: Record<string, { count: number; first: number; last: number }> = {};
  private static readonly AGG_WINDOW_MS = 5000;
  private static readonly FLUSH_INTERVAL_MS = 3000;
  private static aggregatorStarted = false;

  constructor(
    private readonly http: HttpClient,
    private readonly location: LocationStrategy,
    private readonly url: UrlService,
    private readonly environment: EnvironmentService
  ) {
    if (environment.serverLogEnabled()) {
      this.minLogLevel = LogLevel.Warning;
    }
    this.ensureAggregator();
  }

  public debug(cause: string, value?: any, message = '', stackTrace = '') {
    this.log(console.debug, LogLevel.Debug, cause, message, stackTrace, value);
  }

  public info(cause: string, value?: any, message = '', stackTrace = '') {
    this.log(console.info, LogLevel.Info, cause, message, stackTrace, value);
  }

  public warn(cause: string, value?: any, message = '', stackTrace = '') {
    this.log(console.warn, LogLevel.Warning, cause, message, stackTrace, value);
  }

  public error(cause: string, value?: any, message = '', stackTrace = '') {
    this.log(console.error, LogLevel.Error, cause, message, stackTrace, value);
  }

  private log(
    func: (msg: string) => void,
    level: LogLevel,
    cause: string,
    message: string,
    stackTrace: string,
    value?: any
  ): void {
    this.stamp(value);

    const normalized = this.normalizeMessage(message);
    const signature = this.buildSignature(level, cause, normalized, stackTrace, value);

    if (this.isRapidDuplicate(signature)) { return; }

    if (value && typeof value === 'object') {
      try {
        if ((value)[Logger.LOGGED_FLAG]) { return; }
      } catch {}
    }

    if (level < this.minLogLevel) { return; }

    const source = this.location instanceof PathLocationStrategy ? this.location.path() : '';
    let exceptionMessage = normalized;

    if (value) {
      exceptionMessage += JSON.stringify(value, createCircularReplacer(), 2);
    }

    const logDto: ILog = {
      Cause: cause,
      ExceptionMessage: exceptionMessage,
      StackTrace: stackTrace,
      Url: source
    };

    if (level >= LogLevel.Error) {
      this.aggregateError(signature, logDto);
    }

    if (level >= LogLevel.Warning && this.environment.serverLogEnabled()) {
      this.serverLog(logDto)
        .pipe(untilDestroyed(this))
        .subscribe(
          () => { func(`${logDto.Cause} ${logDto.ExceptionMessage}`); },
          () => { console.error(logDto); }
        );
      return;
    }

    func(`${logDto.Cause} ${logDto.ExceptionMessage}`);
  }

  private normalizeMessage(msg: string): string {
    return msg
      ?.replace(/\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}.\d{3}Z/g, '[ts]')
      .replace(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, '[guid]')
      .trim() ?? '';
  }

  private buildSignature(
    level: LogLevel,
    cause: string,
    message: string,
    _stack: string,
    value: any
  ): string {
    const valType =
      value && typeof value === 'object' ? value.constructor?.name ?? 'Object' : typeof value;

    let httpPart = '';
    const candidate = this.extractHttpError(value);
    if (candidate) {
      httpPart = `|HTTP${candidate.status}|${this.stripQuery(candidate.url)}`;
    }

    return `${level}|${cause}|${message}|${valType}${httpPart}`.slice(0, 1000);
  }

  private extractHttpError(value: any): { status: number; url: string } | null {
    if (!value) { return null; }
    if (typeof value === 'object') {
      if ('status' in value && 'url' in value && typeof value.url === 'string') {
        return { status: value.status, url: value.url };
      }

      if (value.error && typeof value.error === 'object' && 'status' in value.error && 'url' in value.error) {
        return { status: value.error.status, url: value.error.url };
      }
    }
    return null;
  }

  private stripQuery(u: string): string {
    try {
      return u.split('?')[0];
    } catch {
      return u;
    }
  }

  private aggregateError(signature: string, _logDto: ILog) {
    const now = Date.now();
    const entry = Logger.aggregate[signature];

    if (!entry) {
      Logger.aggregate[signature] = { count: 1, first: now, last: now };
      return;
    }

    entry.count++;
    entry.last = now;
  }

  private flushAggregate() {
    const now = Date.now();

    for (const [sig, e] of Object.entries(Logger.aggregate)) {
      if (now - e.last >= Logger.AGG_WINDOW_MS) {
        if (e.count > 1) {
          const summary: ILog = {
            Cause: 'RepeatedError',
            ExceptionMessage: `Repeated error (${e.count}x over ${e.last - e.first}ms): ${sig}`,
            StackTrace: '',
            Url: ''
          };

          console.error(summary.ExceptionMessage);

          if (this.environment.serverLogEnabled()) {
            this.serverLog(summary).pipe(untilDestroyed(this)).subscribe();
          }
        }
        delete Logger.aggregate[sig];
      }
    }
  }

  private ensureAggregator() {
    if (Logger.aggregatorStarted) { return; }
    Logger.aggregatorStarted = true;

    timer(Logger.FLUSH_INTERVAL_MS, Logger.FLUSH_INTERVAL_MS)
      .pipe(untilDestroyed(this))
      .subscribe(() => { this.flushAggregate(); });
  }

  private isRapidDuplicate(sig: string): boolean {
    if (Logger.recent.has(sig)) { return true; }
    Logger.recent.add(sig);

    setTimeout(() => Logger.recent.delete(sig), Logger.DUP_WINDOW_MS);

    if (Logger.recent.size > Logger.MAX_RECENT) { Logger.recent.clear(); }

    return false;
  }

  private stamp(v: any, depth = 0): void {
    if (!v || typeof v !== 'object' || depth > 4) { return; }
    try {
      if ((v)[Logger.LOGGED_FLAG]) { return; }
      Object.defineProperty(v, Logger.LOGGED_FLAG, { value: true, enumerable: false });
    } catch {}
    if (Array.isArray(v)) {
      v.forEach(x => { this.stamp(x, depth + 1); });
    } else {
      for (const k of Object.keys(v)) {
        const child = (v)[k];
        if (child && typeof child === 'object') { this.stamp(child, depth + 1); }
      }
    }
  }

  private serverLog(dto: ILog): Observable<any> {
    return this.http.post(this.url.buildServerApiUrl('log'), dto);
  }
}
